import { CTASection } from '../components/CTASection';
import { PageHero } from '../components/PageHero';
import { PricingCard } from '../components/PricingCard';
import { Section, SectionHeading } from '../components/Section';
import { automationPricing, hostingPricing, supportPricing } from '../data/pricing';
import { Icon } from '../lib/icons';
import { SEO } from '../lib/seo';

export function Pricing() {
  return (
    <>
      <SEO
        title="Project Pricing and Hosting Plans"
        description="Planning estimates for MaytoMation automation, custom software, AI systems, managed hosting, infrastructure, and long-term support."
      />
      <PageHero
        eyebrow="Pricing"
        title="Project pricing for practical systems, not open-ended hourly ambiguity."
        body="These ranges are planning estimates. MaytoMation scopes work around business outcomes, existing systems, operational risk, documentation needs, and long-term support expectations."
        primary={{ label: 'Request Scope', href: '/contact' }}
        secondary={{ label: 'Book Consultation', href: '/booking' }}
        highlights={['Planning estimates', 'Project-based scope', 'Support options', 'No hard sell']}
      />

      <Section>
        <div className="rounded-lg border border-brand-border bg-white p-6 shadow-card sm:p-8">
          <div className="flex gap-4">
            <Icon name="fileText" className="mt-1 h-6 w-6 shrink-0 text-brand-teal" />
            <div>
              <h2 className="text-xl font-extrabold text-brand-dark">How to read these numbers</h2>
              <p className="mt-3 text-sm leading-7 text-brand-muted">
                Pricing below is intended for early planning. Final proposals depend on integrations, workflow
                complexity, user roles, data migration, hosting needs, documentation depth, compliance requirements,
                existing system condition, and support expectations. MaytoMation does not emphasize hourly rates
                because business owners need a realistic path to an outcome.
              </p>
            </div>
          </div>
        </div>
      </Section>

      <Section className="bg-white" id="projects">
        <SectionHeading
          eyebrow="Project Estimates"
          title="Automation, software, dashboard, portal, and AI system pricing."
          body="A focused project can be small. A platform that becomes part of daily operations needs deeper design, documentation, testing, and support planning."
        />
        <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {automationPricing.map((item) => (
            <PricingCard key={item.title} {...item} />
          ))}
        </div>
      </Section>

      <Section id="hosting">
        <SectionHeading
          eyebrow="Managed Hosting"
          title="Monthly hosting plans for sites, portals, apps, and infrastructure."
          body="Hosting plans are matched to system importance, monitoring expectations, backup requirements, deployment cadence, and response needs."
        />
        <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {hostingPricing.map((item) => (
            <PricingCard key={item.title} {...item} />
          ))}
        </div>
      </Section>

      <Section className="bg-white" id="support">
        <SectionHeading
          eyebrow="Support"
          title="Long-term support plans for maintainable technology."
          body="Support can include updates, small improvements, vendor coordination, documentation upkeep, monitoring review, troubleshooting, and fractional engineering guidance."
        />
        <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {supportPricing.map((item) => (
            <PricingCard key={item.title} {...item} />
          ))}
        </div>
      </Section>

      <CTASection title="Want a realistic price for your operation?" button="Request a Project Review" />
    </>
  );
}
